import { z } from 'zod';
import { MOODS } from './constants.js';

const httpsUrl = z.string().url().startsWith('https://');
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);

/** Public, immutable snapshot of the recording and terms used in a render. */
export const MusicCreditSchema = z.object({
  title: z.string().trim().min(1).max(100),
  artist: z.string().trim().min(1).max(100),
  sourceUrl: httpsUrl,
  license: z.string().trim().min(1).max(100),
  licenseUrl: httpsUrl,
  attribution: z.string().trim().min(1).max(700),
  attributionRequired: z.boolean().default(true),
  contentId: z.enum(['registered', 'not_registered', 'unknown']),
  contentIdNote: z.string().trim().min(1).max(1000),
});
export type MusicCredit = z.infer<typeof MusicCreditSchema>;

/** Approval concerns our library AND the end user's commercial video rights. */
export const ApprovedMusicRightsSchema = MusicCreditSchema.extend({
  status: z.literal('approved'),
  reviewedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime().nullable(),
  evidenceUrls: z.array(httpsUrl).min(1),
  sourceSha256: sha256,
  permissions: z.object({
    libraryRedistribution: z.literal(true),
    userVideos: z.literal(true),
    monetization: z.literal(true),
    editing: z.literal(true),
  }),
});
export type ApprovedMusicRights = z.infer<typeof ApprovedMusicRightsSchema>;

export const MusicManifestSchema = z.array(z.object({
  file: z.string().regex(/^[^/\\]+\.(mp3|m4a|wav|flac|ogg|aac)$/i),
  downloadUrl: httpsUrl,
  /** Exact member of the author's ZIP, bound to the reviewed archive hash. */
  archive: z.object({
    entry: z.string().min(1).refine(value => !value.startsWith('/') && !value.startsWith('-')
      && !value.split('/').includes('..') && !/[\\*?\[\]\x00]/.test(value), 'Unsafe archive member'),
    sha256,
  }).optional(),
  mood: z.enum(MOODS),
  energy: z.number().min(0).max(1),
  rights: ApprovedMusicRightsSchema,
})).min(1).refine((tracks) => new Set(tracks.map((t) => t.file)).size === tracks.length, 'Duplicate file');

export function approvedMusicRights(value: unknown, now = Date.now()): ApprovedMusicRights | null {
  const parsed = ApprovedMusicRightsSchema.safeParse(value);
  if (!parsed.success) return null;
  const rights = parsed.data;
  if (Date.parse(rights.reviewedAt) > now || (rights.expiresAt && Date.parse(rights.expiresAt) <= now)) return null;
  return rights;
}

export const RenderMusicSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('used'), trackId: z.string(), credit: MusicCreditSchema }),
  z.object({ status: z.literal('unavailable') }),
  z.object({ status: z.literal('disabled') }),
]);
export type RenderMusic = z.infer<typeof RenderMusicSchema>;
export function renderMusic(value: unknown): RenderMusic | null {
  const parsed = RenderMusicSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function musicAttribution(value: unknown): string {
  const music = renderMusic(value);
  return music?.status === 'used' && music.credit.attributionRequired ? music.credit.attribution : '';
}

/** Credits belong to the rendered file, not to the editable/generated caption. */
export function publicationText(caption: string, hashtags: string, music: unknown): string {
  return [caption.trim(), hashtags.trim(), musicAttribution(music)].filter(Boolean).join('\n\n');
}
